import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import GeoPiButton from "@/components/GeoPiButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Activity, Loader2, Download, Upload, FileText, CheckCircle2, AlertTriangle, X, Target, Sparkles, Share2, Copy, SlidersHorizontal, Eye, Trash2, StickyNote, Layers, BarChart3 } from "lucide-react";
import { GeoTiffLayer } from "@/components/GeoTiffLayer";

const SURVEYS = ["Magnetic", "Gravity", "IP / Resistivity", "EM", "Radiometric", "Other"];
const OUT_LABELS = {
  data: "Measured Data (gridded)", regional: "Regional Field", residual: "Residual Anomaly",
  bouguer: "Complete Bouguer Anomaly", terrain_correction: "Terrain Correction (mGal)", elevation: "Station Elevation / DEM",
  rtp: "Reduction to the Pole", vd1: "First Vertical Derivative", vd2: "Second Vertical Derivative",
  thg: "Total Horizontal Gradient", analytic: "Analytic Signal", tilt: "Tilt Derivative", upward: "Upward Continuation",
  chargeability: "Chargeability", resistivity: "Apparent Resistivity", metal_factor: "Metal Factor (MF)",
  conductivity: "Apparent Conductivity", anomaly_overlay: "Interpreted Anomaly Zones",
  conductance: "Apparent Conductance (late-time tau)", depth: "Apparent Conductor Depth",
  ternary: "Radiometric Ternary (K-Th-U)", k: "Potassium (K)", th: "Thorium (Th)", u: "Uranium (U)",
  tc: "Total Count", ratio_kth: "K / Th Ratio",
};

export default function GeophysWorkspace() {
  const nav = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [processed, setProcessed] = useState([]);
  const [combined, setCombined] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState("");
  const [openDs, setOpenDs] = useState(null);
  const [fracPct, setFracPct] = useState(7);
  const [sliderBusy, setSliderBusy] = useState(false);
  const [share, setShare] = useState(null);   // { url, expires_at, views }
  const [expiryDays, setExpiryDays] = useState("");
  const [drill, setDrill] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [portfolio, setPortfolio] = useState(null);   // { url, count }
  const [portExpiry, setPortExpiry] = useState(0);
  const [portIntro, setPortIntro] = useState("");
  const [portStats, setPortStats] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data);
      if (r.data.geophys_coincidence_frac) setFracPct(Math.round(r.data.geophys_coincidence_frac * 100));
      const b = r.data.geophys_batch;
      if (b?.length) {
        setDatasets(b.map((d) => ({ id: d.id, filename: d.filename, survey_type: d.survey_type, qa: d.qa, validation: d.validation, channels_detected: d.channels_detected })));
        if (r.data.geophys_batch_processed) {
          const pr = b.map((d) => ({ id: d.id, filename: d.filename, survey_type: d.survey_type, qa: d.qa, maps: d.maps || {}, zones: d.zones || [], notes: d.notes || [] }));
          setProcessed(pr);
          setOpenDs(pr[0]?.id || null);
          const c = r.data.geophys_combined;
          if (c?.map || r.data.geophys_ai_interpretation) {
            setCombined({ map: c?.map, coincident: c?.coincident || [], top_targets: c?.top_targets || [], ai_interpretation: r.data.geophys_ai_interpretation });
          }
        }
      }
    }).catch(() => {});
    api.get(`/geophys/${id}/batch/share`).then((r) => {
      if (r.data?.active) setShare({ url: `${window.location.origin}${r.data.path}`, expires_at: r.data.expires_at, views: r.data.views || 0 });
    }).catch(() => {});
    api.get(`/geophys/portfolio/share`).then((r) => {
      if (r.data?.active) {
        setPortfolio({ url: `${window.location.origin}${r.data.path}`, count: r.data.teaser_count, expires_at: r.data.expires_at, views: r.data.views || 0, last_viewed: r.data.last_viewed });
        if (r.data.intro) setPortIntro(r.data.intro);
      }
    }).catch(() => {});
  }, [id]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy("upload"); setDatasets([]); setProcessed([]); setOverrides({}); setCombined(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/geophys/${id}/batch/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDatasets(data.datasets || []);
      const ok = (data.datasets || []).filter((d) => d.survey_type).length;
      const bad = (data.datasets || []).length - ok;
      toast.success(`Detected ${ok} dataset(s)${bad ? ` · ${bad} could not be read` : ""} - review the methods, then process`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const processAll = async () => {
    const list = datasets.filter((d) => d.survey_type);
    if (!list.length) return;
    setBusy("process"); setProcessed([]); setCombined(null);
    setProgress({ done: 0, total: list.length, label: overrides[list[0].id] || list[0].survey_type, file: list[0].filename });
    const results = [];
    try {
      for (let i = 0; i < list.length; i++) {
        const d = list[i];
        const st = overrides[d.id] || d.survey_type;
        setProgress({ done: i, total: list.length, label: st, file: d.filename });
        const { data } = await api.post(`/geophys/${id}/batch/process-one`, { id: d.id, survey_type: st });
        results.push({ id: d.id, filename: d.filename, survey_type: data.survey_type, qa: d.qa, maps: data.maps || {}, zones: data.zones || [], notes: data.notes || [] });
        setProcessed([...results]);
      }
      setOpenDs(results[0]?.id || null);
      setProgress({ done: list.length, total: list.length, label: "Multi-method target map & AI interpretation", file: "" });
      const { data: comb } = await api.get(`/geophys/${id}/batch/combined-map`, { params: { frac: fracPct / 100 } });
      setCombined(comb);
      toast.success(`Processed ${results.length} dataset(s) · ${comb.coincident?.length || 0} coincident target(s)`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); setProgress(null); }
  };

  const recompute = async (p) => {
    setSliderBusy(true);
    try {
      const { data } = await api.get(`/geophys/${id}/batch/combined-map`, { params: { frac: p / 100, ai: false } });
      setCombined((c) => ({ ...(c || {}), map: data.map, coincident: data.coincident, top_targets: data.top_targets }));
      toast.success(`${data.coincident?.length || 0} coincident target(s) at ${p}% sensitivity`);
    } catch (e) { toast.error(apiError(e)); } finally { setSliderBusy(false); }
  };

  const doShare = async (days) => {
    setBusy("share");
    try {
      const { data } = await api.post(`/geophys/${id}/batch/share`, { expires_days: days || undefined });
      const url = `${window.location.origin}/geo-teaser/${data.token}`;
      setShare({ url, expires_at: data.expires_at, views: data.views || 0 });
      try { await navigator.clipboard.writeText(url); toast.success("Shareable link copied to clipboard"); }
      catch { toast.success("Shareable link created"); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const savePortfolio = async () => {
    setBusy("portfolio");
    try {
      const { data } = await api.post(`/geophys/portfolio/share`, { expires_days: portExpiry || undefined, intro: portIntro || undefined });
      const url = `${window.location.origin}/geo-portfolio/${data.token}`;
      setPortfolio((prev) => ({ url, count: prev?.count ?? 0, expires_at: data.expires_at }));
      try { await navigator.clipboard.writeText(url); toast.success("Portfolio link saved & copied"); }
      catch { toast.success("Portfolio link saved"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save portfolio link"); } finally { setBusy(""); }
  };
  const revokePortfolio = async () => {
    try { await api.delete(`/geophys/portfolio/share`); setPortfolio(null); setPortStats(null); toast.success("Portfolio link moved to Trash - restore within 24h from Projects → Trash"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };
  const toggleAnalytics = async () => {
    if (portStats) { setPortStats(null); return; }
    setBusy("stats");
    try { const { data } = await api.get(`/geophys/portfolio/analytics`); setPortStats(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load analytics"); }
    finally { setBusy(""); }
  };

  const revokeShare = async () => {
    setBusy("revoke");
    try { await api.delete(`/geophys/${id}/batch/share`); setShare(null); toast.success("Link moved to Trash - restore within 24h from Projects → Trash"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const openDrill = (c) => { setDrill(c); setNoteText(c.note || ""); };
  const saveNote = async () => {
    setBusy("note");
    try {
      const { data } = await api.put(`/geophys/${id}/batch/target-note`, { id: drill.id, note: noteText });
      const patch = (arr) => (arr || []).map((t) => (t.id === drill.id ? { ...t, note: data.note || null } : t));
      setCombined((c) => ({ ...(c || {}), coincident: patch(c?.coincident), top_targets: patch(c?.top_targets) }));
      setDrill((d) => ({ ...d, note: data.note || null }));
      toast.success(data.note ? "Note saved" : "Note removed");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const dlReport = async () => {
    setBusy("report");
    try { await downloadBlob(`/geophys/${id}/batch/report`, `SYMIN_integrated_geophysics.pdf`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const dlZip = async () => {
    setBusy("zip");
    try { await downloadBlob(`/geophys/${id}/batch/download-all`, `SYMIN_integrated_geophysics.zip`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const validCount = datasets.filter((d) => d.survey_type).length;
  const totalAnoms = processed.reduce((s, d) => s + (d.zones?.length || 0), 0);
  const pct = progress ? Math.round((progress.done / Math.max(progress.total, 1)) * 100) : 0;
  const overlayFor = (dsId) => processed.find((d) => d.id === dsId)?.maps?.anomaly_overlay;
  const expLabel = share?.expires_at ? `Expires ${new Date(share.expires_at).toLocaleDateString()}` : "No expiry";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="min-h-16 bg-white border-b border-slate-200 flex items-center flex-wrap gap-x-4 gap-y-1 px-4 sm:px-6 py-2">
        <button onClick={() => nav("/console")} data-testid="back-to-modes" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={16} /> Back to Console</button>
        <button onClick={() => nav("/geophysics")} data-testid="geophys-back" className="text-sm text-slate-500 hover:text-slate-900 border-l border-slate-200 pl-4">Projects</button>
        <div className="hidden sm:block h-5 w-px bg-slate-200" />
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 min-w-0"><Activity size={16} className="text-orange-500 shrink-0" /> <span className="truncate">SYMIN &gt; {project?.name || "Geophysics"}</span></div>
        <div className="ml-auto"><GeoPiButton compact project={{ id: project?.id || id, name: project?.name || "Geophysics project", context: `Module: Geophysics (magnetics, gravity, IP/resistivity, EM, radiometrics processing & interpretation). Project: ${project?.name || ""}.` }} /></div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* STEP 01 - MULTI-FILE UPLOAD */}
        <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="geo-step-upload">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">01 - Upload Geophysical Data (multiple files)</div>
          <p className="text-sm text-slate-500 mb-4">Select several data files at once (CSV, TXT, XYZ, DAT, or Excel .xlsx/.xls). The survey type and X/Y/Value columns are auto-detected for each file - review below, then process them all together into one integrated report.</p>
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 h-10 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:border-orange-400">
            <input type="file" multiple accept=".csv,.txt,.xyz,.dat,.xlsx,.xls" className="hidden" data-testid="geo-batch-upload-input" onChange={(e) => uploadFiles(e.target.files)} />
            {busy === "upload" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Choose Data Files
          </label>

          {datasets.length > 0 && (
            <div className="mt-5" data-testid="geo-batch-review">
              <div className="text-sm font-semibold text-slate-800 mb-2">Auto-Detected Datasets ({validCount})</div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-semibold">File</th>
                      <th className="px-3 py-2 font-semibold">Detected Method</th>
                      <th className="px-3 py-2 font-semibold">Records</th>
                      <th className="px-3 py-2 font-semibold">Range</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datasets.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100" data-testid={`geo-ds-row-${d.id}`}>
                        <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate" title={d.filename}>{d.filename}</td>
                        <td className="px-3 py-2">
                          {d.survey_type ? (
                            <select data-testid={`geo-ds-method-${d.id}`} value={overrides[d.id] || d.survey_type}
                              onChange={(e) => setOverrides({ ...overrides, [d.id]: e.target.value })}
                              className="h-8 px-2 rounded-md border border-slate-300 text-sm bg-white">
                              {SURVEYS.map((s) => <option key={s} value={s} label={s} />)}
                            </select>
                          ) : <span className="text-red-500 text-xs">unreadable</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600" data-testid={`geo-ds-records-${d.id}`}>{d.qa?.records ?? "-"}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs">{d.qa ? `${d.qa.value_min} → ${d.qa.value_max}` : "-"}</td>
                        <td className="px-3 py-2">
                          {d.validation?.ok
                            ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><CheckCircle2 size={13} /> OK</span>
                            : <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium" title={(d.validation?.errors || []).join("; ")}><AlertTriangle size={13} /> Review</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button data-testid="geo-process-all" onClick={processAll} disabled={busy === "process" || !validCount}
                className="mt-4 bg-orange-500 hover:bg-orange-600 text-white h-10 px-6">
                {busy === "process" ? <><Loader2 size={15} className="mr-2 animate-spin" />Processing…</> : `Process ${validCount} Dataset(s)`}
              </Button>

              {progress && (
                <div className="mt-4" data-testid="geo-progress">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span><span className="font-medium text-slate-700">Processing {progress.label}…</span>{progress.file ? ` (${progress.file})` : ""} - {Math.min(progress.done + 1, progress.total)} of {progress.total}</span>
                    <span data-testid="geo-progress-pct">{pct}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${pct}%` }} data-testid="geo-progress-bar" />
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <GeoTiffLayer pid={id} boundary={project?.boundary} />

        {/* STEP 02 - RESULTS */}
        {processed.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="geo-batch-results">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">02 - Results & Integrated Report</div>
                <div className="text-sm text-slate-500 mt-0.5">{processed.length} dataset(s) · {totalAnoms} anomaly zone(s){combined?.coincident?.length ? ` · ${combined.coincident.length} coincident target(s)` : ""}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="geo-portfolio-btn" onClick={savePortfolio} disabled={busy === "portfolio"} variant="outline" className="h-9 border-[#C20511]/50 text-[#8a6a1f] hover:bg-red-50"><Layers size={14} className="mr-2" />{busy === "portfolio" ? "Saving…" : portfolio ? "Portfolio ✓" : "Client Portfolio"}</Button>
                <Button data-testid="geo-share-btn" onClick={() => doShare(expiryDays)} disabled={busy === "share"} variant="outline" className="h-9 border-red-300 text-red-700 hover:bg-red-50"><Share2 size={14} className="mr-2" />{busy === "share" ? "Creating…" : share ? "Re-share" : "Share Targets"}</Button>
                <Button data-testid="geo-batch-report" onClick={dlReport} disabled={busy === "report"} className="bg-slate-900 hover:bg-slate-800 text-white h-9"><FileText size={14} className="mr-2" />{busy === "report" ? "Building…" : "Integrated Report"}</Button>
                <Button data-testid="geo-batch-download-all" onClick={dlZip} disabled={busy === "zip"} className="bg-slate-900 hover:bg-slate-800 text-white h-9"><Download size={14} className="mr-2" />{busy === "zip" ? "Zipping…" : "Download All"}</Button>
              </div>
            </div>

            {share && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="geo-share-box">
                <div className="flex items-center gap-2">
                  <Share2 size={14} className="text-red-600 shrink-0" />
                  <input readOnly value={share.url} data-testid="geo-share-url" className="flex-1 bg-transparent text-xs text-slate-700 outline-none min-w-0" onFocus={(e) => e.target.select()} />
                  <button data-testid="geo-share-copy" onClick={() => { navigator.clipboard?.writeText(share.url); toast.success("Copied"); }} className="text-red-700 hover:text-red-900" title="Copy"><Copy size={14} /></button>
                  <a href={share.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-red-700 hover:text-red-900 whitespace-nowrap">Open ↗</a>
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-red-800/90">
                  <span className="inline-flex items-center gap-1" data-testid="geo-share-views"><Eye size={13} /> Opened {share.views}×</span>
                  <span>· {expLabel}</span>
                  <label className="inline-flex items-center gap-1 ml-auto">
                    <span className="text-slate-500">Expiry</span>
                    <select data-testid="geo-share-expiry" value={expiryDays} onChange={(e) => { setExpiryDays(e.target.value); doShare(e.target.value); }}
                      className="h-7 px-1.5 rounded border border-red-300 bg-white text-slate-700">
                      <option value="">Never</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </label>
                  <button data-testid="geo-share-revoke" onClick={revokeShare} disabled={busy === "revoke"} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"><Trash2 size={13} /> Revoke</button>
                </div>
              </div>
            )}

            {portfolio && (
              <div className="mb-5 rounded-lg border border-[#C20511]/40 bg-[#faf6ec] p-3" data-testid="geo-portfolio-box">
                <div className="flex items-center gap-2">
                  <Layers size={14} className="text-[#C20511] shrink-0" />
                  <input readOnly value={portfolio.url} data-testid="geo-portfolio-url" className="flex-1 bg-transparent text-xs text-slate-700 outline-none min-w-0" onFocus={(e) => e.target.select()} />
                  <button data-testid="geo-portfolio-copy" onClick={() => { navigator.clipboard?.writeText(portfolio.url); toast.success("Copied"); }} className="text-[#8a6a1f] hover:text-[#5f4713]" title="Copy"><Copy size={14} /></button>
                  <a href={portfolio.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-[#8a6a1f] hover:text-[#5f4713] whitespace-nowrap">Open ↗</a>
                </div>
                <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                  <div>
                    <label className="block text-[11px] font-semibold text-[#8a6a1f] mb-1">Intro shown to clients (optional)</label>
                    <textarea data-testid="geo-portfolio-intro" value={portIntro} onChange={(e) => setPortIntro(e.target.value.slice(0, 600))} rows={2}
                      placeholder="e.g. A selection of our latest priority targets across the Lake Victoria Goldfields."
                      className="w-full text-xs rounded-md border border-[#C20511]/40 bg-white/70 p-2 text-slate-700 outline-none" />
                  </div>
                  <div className="flex items-center gap-2">
                    <select data-testid="geo-portfolio-expiry" value={portExpiry} onChange={(e) => setPortExpiry(Number(e.target.value))}
                      className="h-9 px-2 rounded-md border border-[#C20511]/40 bg-white/70 text-xs text-slate-700">
                      <option value={0}>No expiry</option>
                      <option value={7}>7 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                    </select>
                    <Button data-testid="geo-portfolio-save" onClick={savePortfolio} disabled={busy === "portfolio"} size="sm" className="h-9 bg-[#C20511] hover:bg-[#b1852f] text-white">Save</Button>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-[#8a6a1f]">
                  <span>One branded gallery of every shared target teaser{typeof portfolio.count === "number" ? ` · ${portfolio.count} published` : ""}{portfolio.expires_at ? ` · expires ${String(portfolio.expires_at).slice(0, 10)}` : " · no expiry"}</span>
                  <span className="inline-flex items-center gap-1" data-testid="geo-portfolio-views"><Eye size={13} /> Opened {portfolio.views ?? 0}×</span>
                  <button data-testid="geo-portfolio-analytics-btn" onClick={toggleAnalytics} disabled={busy === "stats"}
                    className="inline-flex items-center gap-1 font-medium text-[#8a6a1f] hover:text-[#5f4713]">
                    <BarChart3 size={13} /> {busy === "stats" ? "Loading…" : portStats ? "Hide analytics" : "Analytics"}
                  </button>
                  <button data-testid="geo-portfolio-revoke" onClick={revokePortfolio} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium ml-auto"><Trash2 size={13} /> Revoke</button>
                </div>
                {portStats && (
                  <div className="mt-3 rounded-md border border-[#C20511]/30 bg-white/60 p-3" data-testid="geo-portfolio-stats">
                    <div className="text-[11px] font-semibold text-[#8a6a1f] mb-2">
                      Portfolio opened {portStats.portfolio_views}×{portStats.portfolio_last_viewed ? ` · last viewed ${String(portStats.portfolio_last_viewed).slice(0, 10)}` : ""}
                    </div>
                    {(portStats.teasers || []).length === 0 ? (
                      <div className="text-xs text-slate-500">No shared teasers yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {portStats.teasers.map((t, i) => (
                          <div key={t.token || i} className="flex items-start gap-2 flex-wrap text-xs" data-testid={`geo-portfolio-stat-row-${i}`}>
                            <span className={`font-semibold truncate max-w-[220px] ${t.expired ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.name || "Untitled"}</span>
                            <span className="inline-flex items-center gap-1 text-slate-500"><Eye size={11} /> {t.views}×{t.last_viewed ? ` · ${String(t.last_viewed).slice(0, 10)}` : ""}</span>
                            {(t.targets || []).some((tg) => tg.views > 0) && (
                              <span className="flex flex-wrap gap-1">
                                {t.targets.filter((tg) => tg.views > 0).map((tg) => (
                                  <span key={tg.id} className="rounded-full bg-[#C20511]/15 text-[#8a6a1f] px-1.5 py-0.5 text-[10px] font-semibold">{tg.id} ×{tg.views}</span>
                                ))}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {(portStats.visits || []).length > 0 && (
                      <div className="mt-3 pt-2 border-t border-[#C20511]/20" data-testid="geo-portfolio-visits">
                        <div className="text-[11px] font-semibold text-[#8a6a1f] mb-1.5">Recent visits</div>
                        <div className="space-y-1">
                          {portStats.visits.map((v, i) => (
                            <div key={i} className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600" data-testid={`geo-visit-row-${i}`}>
                              <span className="text-slate-400 font-mono shrink-0">{v.ts ? new Date(v.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                              <span className="rounded-full bg-slate-200/70 text-slate-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase">{v.kind === "portfolio" ? "Portfolio" : v.kind === "teaser" ? "Teaser" : `Target ${v.target_id || ""}`}</span>
                              <span className="font-semibold text-slate-700">{v.where || "Unknown location"}</span>
                              {v.project_name && v.kind !== "portfolio" && <span className="text-slate-400 truncate max-w-[180px]">· {v.project_name}</span>}
                              {v.device && <span className="text-slate-400">· {v.device}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Multi-method target map + sensitivity + coincident targets */}
            {combined?.map && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50/40 overflow-hidden" data-testid="geo-combined-card">
                <div className="flex items-center gap-2 px-4 pt-3 text-sm font-semibold text-slate-800"><Target size={15} className="text-orange-500" /> Multi-Method Target Map</div>
                <p className="px-4 text-xs text-slate-500">Anomalies from every survey overlaid - gold stars mark coincident, multi-method targets. Click a target below to inspect it or add a note.</p>
                <img src={combined.map} alt="multi-method target map" className="w-full object-contain bg-white mt-2" data-testid="geo-combined-map" />

                <div className="px-4 py-3 border-t border-red-200/70">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                    <span className="inline-flex items-center gap-1.5 font-medium"><SlidersHorizontal size={13} className="text-orange-500" /> Coincidence sensitivity {sliderBusy && <Loader2 size={12} className="animate-spin" />}</span>
                    <span data-testid="geo-sensitivity-value">{fracPct}% of survey extent {fracPct <= 5 ? "· tight" : fracPct >= 14 ? "· loose" : ""}</span>
                  </div>
                  <Slider data-testid="geo-sensitivity-slider" value={[fracPct]} min={2} max={20} step={1}
                    onValueChange={(v) => setFracPct(v[0])} onValueCommit={(v) => recompute(v[0])} className="py-1" />
                  <p className="text-[11px] text-slate-400 mt-1">Lower = anomalies must sit closer together to count as one coincident target.</p>
                </div>

                {combined.coincident?.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-4 pb-3" data-testid="geo-coincident-list">
                    {combined.coincident.map((c) => (
                      <button key={c.id} data-testid={`geo-coincident-${c.id}`} onClick={() => openDrill(c)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-red-300 hover:border-red-500 hover:bg-red-50 text-slate-700 rounded-full px-3 py-1 transition-colors">
                        <span className="text-red-500">★</span> {c.id}: {c.methods?.join(" + ")} <span className="text-slate-400">· {c.score ?? "-"}</span>
                        {c.note ? <span className="h-1.5 w-1.5 rounded-full bg-red-500" title="Has note" /> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {combined?.ai_interpretation && (
              <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="geo-ai-interpretation">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-2"><Sparkles size={15} className="text-orange-500" /> AI Integrated Interpretation</div>
                <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
                  {String(combined.ai_interpretation).split("\n\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {processed.map((d) => (
                <div key={d.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`geo-ds-result-${d.id}`}>
                  <button onClick={() => setOpenDs(openDs === d.id ? null : d.id)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{d.id} · {d.survey_type} <span className="text-slate-400 font-normal">- {d.filename}</span></div>
                      <div className="text-xs text-slate-500">{Object.keys(d.maps || {}).length} map(s) · {d.zones?.length || 0} anomaly zone(s)</div>
                    </div>
                    <span className="text-slate-400 text-xs shrink-0 ml-3">{openDs === d.id ? <X size={16} /> : "View"}</span>
                  </button>
                  {openDs === d.id && (
                    <div className="p-4">
                      {Object.keys(d.maps || {}).length === 0 ? (
                        <div className="text-sm text-red-600">{(d.notes || []).join(" ") || "No maps were produced for this dataset."}</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {Object.entries(d.maps).map(([k, uri]) => (
                            <div key={k} className="border border-slate-200 rounded-lg overflow-hidden">
                              <img src={uri} alt={k} className="w-full object-contain bg-slate-50" data-testid={`geo-img-${d.id}-${k}`} />
                              <div className="px-3 py-2 text-sm font-medium text-slate-700">{OUT_LABELS[k] || k}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Target drill-down + note */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="geo-drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target size={16} className="text-orange-500" /> {drill?.id} - Coincident Target</DialogTitle>
            <DialogDescription>Anomalies from independent survey methods that coincide spatially to form this integrated exploration target.</DialogDescription>
          </DialogHeader>
          {drill && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-3 py-1"><b>{drill.n_methods || drill.methods?.length}</b> methods agree</span>
                <span className="rounded-full bg-red-100 text-red-800 px-3 py-1">Score <b>{drill.score ?? "-"}</b></span>
                <span className="rounded-full bg-slate-100 px-3 py-1">Priority <b>{drill.priority || "-"}</b></span>
                <span className="rounded-full bg-slate-100 px-3 py-1">{Number(drill.lat).toFixed(5)}, {Number(drill.lon).toFixed(5)}</span>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5"><StickyNote size={14} className="text-orange-500" /> Geologist note</div>
                <Textarea data-testid="geo-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={400}
                  placeholder="Add a short note for this target - it appears in the drill-down, the shared client teaser and the report." className="bg-white text-sm" rows={2} />
                <div className="flex justify-end mt-2">
                  <Button data-testid="geo-note-save" size="sm" onClick={saveNote} disabled={busy === "note"} className="h-8 bg-slate-900 hover:bg-slate-800 text-white">{busy === "note" ? "Saving…" : "Save note"}</Button>
                </div>
              </div>

              <p className="text-sm text-slate-500">Anomalies from each method that combine to form this target:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(drill.members || []).map((m, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`geo-drill-member-${i}`}>
                    {overlayFor(m.ds_id)
                      ? <img src={overlayFor(m.ds_id)} alt={m.method} className="w-full object-contain bg-slate-50" />
                      : <div className="aspect-[4/3] grid place-items-center bg-slate-50 text-slate-300 text-xs">No overlay</div>}
                    <div className="p-3">
                      <div className="text-sm font-semibold text-slate-800">{m.method} · {m.id}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Priority {m.priority || "-"} · strength {m.strength ?? "-"}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{Number(m.lat).toFixed(5)}, {Number(m.lon).toFixed(5)}</div>
                      <div className="text-[11px] text-slate-400 truncate" title={m.filename}>{m.filename}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
